import { Checkbox } from "@mui/material";
import { useCallback, useMemo } from "react";
import Table from "../../components/common/table/Table";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { useAppConfig } from "../../context/AppConfigContext";
import {
  getStoreAddressFields,
  getStoreHeaderData,
  MESSAGES,
  STYLES,
  TABLE_CONFIG,
} from "./storeConstants";
import { getStoreFilterSchema, getStoreFilterUISchema } from "./storeFormSchema";
import "./storeManagement.scss";
import { transformStoresToRowData } from "./storeUtils";

const StoresTable = ({
  stores,
  loading,
  filterData,
  handleEdit,
  handleView,
  handleSoftDelete,
  handleFilterChange,
  selectable = false,
  selectedStoreIds = [],
  onSelectionChange,
  isSyncMarket = false,
}) => {
  const { appConfig } = useAppConfig();
  const addressFields = useMemo(() => getStoreAddressFields(appConfig), [appConfig]);

  const baseHeaderData = useMemo(
    () => getStoreHeaderData(addressFields),
    [addressFields]
  );

  const filterSchema = useMemo(
    () => getStoreFilterSchema(addressFields),
    [addressFields]
  );
  const filterUISchema = useMemo(
    () => getStoreFilterUISchema(addressFields),
    [addressFields]
  );

  const rowData = transformStoresToRowData(
    stores,
    handleEdit,
    handleSoftDelete,
    handleView,
    isSyncMarket
  );

  const allIds = useMemo(() => stores.map((s) => s.id), [stores]);
  const allSelected =
    selectable &&
    allIds.length > 0 &&
    allIds.every((id) => selectedStoreIds.includes(id));
  const someSelected = selectable && selectedStoreIds.length > 0 && !allSelected;

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...allIds]);
    }
  }, [allSelected, allIds, onSelectionChange]);

  const handleToggleRow = useCallback(
    (storeId) => {
      if (!onSelectionChange) return;
      if (selectedStoreIds.includes(storeId)) {
        onSelectionChange(selectedStoreIds.filter((id) => id !== storeId));
      } else {
        onSelectionChange([...selectedStoreIds, storeId]);
      }
    },
    [selectedStoreIds, onSelectionChange]
  );

  const headerData = useMemo(() => {
    if (!selectable) return baseHeaderData;

    const checkboxHeader = {
      columnKey: "checkbox",
      header: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleToggleAll}
          size="small"
          sx={{ padding: 0 }}
        />
      ),
      className: "checkbox-col",
    };
    return [checkboxHeader, ...baseHeaderData];
  }, [selectable, allSelected, someSelected, handleToggleAll, baseHeaderData]);

  const enhancedRowData = useMemo(() => {
    if (!selectable) return rowData;

    return rowData.map((row, index) => {
      const store = stores[index];
      return {
        checkbox: (
          <Checkbox
            checked={selectedStoreIds.includes(store?.id)}
            onChange={() => handleToggleRow(store?.id)}
            size="small"
            sx={{ padding: 0 }}
          />
        ),
        ...row,
      };
    });
  }, [selectable, rowData, stores, selectedStoreIds, handleToggleRow]);

  return (
    <div className="stores-table-container">
      <div className="stores-table-header">
        <div className="filter-wrapper">
          <JsonFormsWrapper
            jsonschema={filterSchema}
            uischema={filterUISchema}
            data={filterData}
            onChange={handleFilterChange}
          />
        </div>
      </div>
      {loading ? (
        <div style={STYLES.loadingContainer}>{MESSAGES.LOADING}</div>
      ) : (
        <Table
          headerData={headerData}
          rowData={enhancedRowData}
          className="stores-table"
          pagination={true}
          rowsPerPage={TABLE_CONFIG.ROWS_PER_PAGE}
          noDataLabel={TABLE_CONFIG.NO_DATA_LABEL}
        />
      )}
    </div>
  );
};

export default StoresTable;
